import { apiHandler } from "../buyerApiHandler";

/**
 * Wallet promotions: cashback, referrals, promo codes and top-up bonuses.
 * Same path as the wallet admin client - straight to the Buyer API with
 * x-api-key. Every switch here is off until someone turns it on.
 */

const BASE = "/admin/wallets/promotions";

/** The Buyer API wraps responses as { success, data }. */
const unwrap = <T,>(res: any): T => (res?.data ?? res) as T;

export interface PromotionSettings {
  cashback_enabled: boolean;
  cashback_percent: number;
  cashback_max_amount: number;
  cashback_min_order: number;
  cashback_hold_hours: number;
  cashback_expiry_days: number;
  cashback_orders: "all" | "online";
  referral_enabled: boolean;
  referral_referrer_amount: number;
  referral_referee_amount: number;
  referral_min_order: number;
  referral_expiry_days: number;
  promo_codes_enabled: boolean;
  promo_default_expiry_days: number;
  promo_expiry_reminder_days: number;
}

export interface PromoCodeRow {
  id: number;
  code: string;
  amount: number;
  expires_in_days: number;
  min_order_value: number;
  max_redemptions: number | null;
  per_user_limit: number;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  redeemed: number;
  created_at: string;
}

export interface BonusTierRow {
  id: number;
  min_amount: number;
  bonus_amount: number;
  expires_in_days: number;
  min_order_value: number;
  is_active: boolean;
}

export interface PromotionReport {
  promo_given: number;
  cashback_given: number;
  referral_given: number;
  expired: number;
  outstanding_lots: number;
  scheduled: { count: number; amount: number };
}

const json = { headers: { "Content-Type": "application/json" } };

export const getPromotionSettings = async (): Promise<PromotionSettings> =>
  unwrap(await apiHandler("get", `${BASE}/settings`));

export const savePromotionSettings = async (
  settings: PromotionSettings,
): Promise<PromotionSettings> =>
  unwrap(await apiHandler("put", `${BASE}/settings`, settings, json));

export const getPromotionReport = async (): Promise<PromotionReport> =>
  unwrap(await apiHandler("get", `${BASE}/report`));

export const getPromoCodes = async (): Promise<PromoCodeRow[]> =>
  unwrap(await apiHandler("get", `${BASE}/codes`));

export const savePromoCode = async (
  code: Partial<PromoCodeRow>,
): Promise<PromoCodeRow[]> =>
  unwrap(await apiHandler("post", `${BASE}/codes`, code, json));

export const getBonusTiers = async (): Promise<BonusTierRow[]> =>
  unwrap(await apiHandler("get", `${BASE}/bonus-tiers`));

export const saveBonusTier = async (
  tier: Partial<BonusTierRow>,
): Promise<BonusTierRow[]> =>
  unwrap(await apiHandler("post", `${BASE}/bonus-tiers`, tier, json));

export const deleteBonusTier = async (id: number): Promise<BonusTierRow[]> =>
  unwrap(await apiHandler("delete", `${BASE}/bonus-tiers/${id}`));

export const giveCompensation = async (payload: {
  user_id: number;
  amount: number;
  reason: string;
  order_id?: number;
  expires_in_days?: number;
  /** One key per opened dialog, so a double click credits only once. */
  idempotency_key?: string;
}): Promise<{
  credited: boolean;
  duplicate?: boolean;
  expires_in_days: number;
  txn_ref: string;
}> =>
  unwrap(await apiHandler("post", `${BASE}/compensate`, payload, json));
